import { api } from "./api";

/**
 * Funboard API helpers (v79.106).
 *
 * Every call already lives under REACT_APP_BACKEND_URL/api via the
 * shared `api` axios instance, so the caller passes only the tail.
 */
export const funboardApi = {
    feed: (params = {}) => api.get("/funboard/feed", { params }).then((r) => r.data),
    react: (postId, kind) => api.post(`/funboard/posts/${postId}/react`, { kind }).then((r) => r.data),
    share: (postId) => api.post(`/funboard/posts/${postId}/share`).then((r) => r.data),
    // v79.181 — Item 3: Repost engine — rebroadcast to your own Gang timeline.
    repost: (postId, caption) => api.post(`/funboard/posts/${postId}/repost`, { post_id: postId, caption }).then((r) => r.data),
    // v79.185 — Pin post to permanent profile-timeline (survives 30-day Gang TTL).
    pinAchievement: (postId, note) => api.post(`/achievements/pin/${postId}`, { note: note || null }).then((r) => r.data),
    unpinAchievement: (achievementId) => api.delete(`/achievements/${achievementId}`).then((r) => r.data),
    myAchievements: () => api.get(`/achievements/mine`).then((r) => r.data),
    userAchievements: (userId) => api.get(`/achievements/user/${userId}`).then((r) => r.data),
    // v79.183 — Server-side rich link preview (FB / IG / Reels / YT / TT / X).
    linkPreview: (url) => api.post(`/link-preview`, { url }).then((r) => r.data),
    // v79.183 — Gemini 3 Flash powered 3-reply smart suggestions.
    smartReplies: (threadId, messages) => api.post(`/ai/chat/smart-replies`, { thread_id: threadId, messages }).then((r) => r.data),
    // v79.183 — Universal admin listing override (Edit Live).
    adminPatchListing: (listingId, payload) => api.patch(`/admin/listings/${listingId}`, payload).then((r) => r.data),
    save: (postId) => api.post(`/funboard/posts/${postId}/save`).then((r) => r.data),
    report: (postId, reason) => api.post(`/funboard/posts/${postId}/report`, { reason }).then((r) => r.data),
    shareListing: (listingId, caption) => api.post("/funboard/share-listing", { listing_id: listingId, caption }).then((r) => r.data),
    userSubmit: (url, caption) => api.post("/funboard/user-submit", { url, caption }).then((r) => r.data),
    userDelete: (postId) => api.delete(`/funboard/user-submit/${postId}`).then((r) => r.data),
    getPrefs: () => api.get("/funboard/preferences").then((r) => r.data),
    setPrefs: (categories) => api.post("/funboard/preferences", { categories }).then((r) => r.data),
    my: (tab = "saved") => api.get("/funboard/my", { params: { tab } }).then((r) => r.data),
    // v79.152 — Founder-detailed My Funboard: user's OWN posts with
    // per-post reactions, comment count, save count, share count.
    myPosts: (limit = 100) => api.get("/funboard/my-posts", { params: { limit } }).then((r) => r.data),
    // v79.153 — Edit own post (title/body/images/visibility).
    updatePost: (postId, patch) => api.patch(`/funboard/posts/${postId}`, patch).then((r) => r.data),
    // v79.153 — Comments (with per-comment like/dislike/reply).
    listComments: (postId, { limit = 3, all = false } = {}) => api.get(`/funboard/posts/${postId}/comments`, { params: { limit, all_replies: all ? 1 : 0 } }).then((r) => r.data),
    addComment: (postId, body, parentId = null) => api.post(`/funboard/posts/${postId}/comments`, { body, parent_id: parentId }).then((r) => r.data),
    deleteComment: (commentId) => api.delete(`/funboard/comments/${commentId}`).then((r) => r.data),
    reactComment: (commentId, kind) => api.post(`/funboard/comments/${commentId}/react`, { kind }).then((r) => r.data),
    myCommentReactions: (postId) => api.get(`/funboard/posts/${postId}/comments/my-reactions`).then((r) => r.data),
    trendingHashtags: (params = {}) => api.get("/funboard/hashtags/trending", { params }).then((r) => r.data),
};

/** Human-readable labels + emoji for the interest picker. Order matters. */
export const FUNBOARD_CATEGORIES = [
    // v79.158 — "Adukoo Gang" — the single home for all user-written
    // posts. Sits at the top so users always find their tribe first.
    { id: "adukoo_gang",   label: "Adukoo Gang",   emoji: "👑" },
    { id: "news",          label: "News",          emoji: "📰" },
    { id: "world",         label: "World",         emoji: "🌍" },
    { id: "politics",      label: "Politics",      emoji: "🏛️" },
    { id: "sports",        label: "Sports",        emoji: "⚽" },
    { id: "movies",        label: "Movies",        emoji: "🎬" },
    { id: "ott",           label: "OTT",           emoji: "📺" },
    { id: "entertainment", label: "Entertainment", emoji: "🎭" },
    { id: "memes",         label: "Memes",         emoji: "😂" },
    { id: "photos",        label: "Photos",        emoji: "📸" },
    { id: "quotes",        label: "Quotes",        emoji: "💬" },
    { id: "animals",       label: "Animals",       emoji: "🐾" },
    { id: "gaming",        label: "Gaming",        emoji: "🎮" },
    { id: "tech",          label: "Tech",          emoji: "💻" },
    { id: "science",       label: "Science",       emoji: "🔬" },
    { id: "health",        label: "Health",        emoji: "🩺" },
    { id: "business",      label: "Business",      emoji: "📈" },
    { id: "auto",          label: "Auto",          emoji: "🚗" },
    { id: "travel",        label: "Travel",        emoji: "✈️" },
    { id: "fashion",       label: "Fashion",       emoji: "👗" },
    { id: "food",          label: "Food",          emoji: "🍔" },
    { id: "lifestyle",     label: "Lifestyle",     emoji: "🌿" },
];

/** Region dropdown (Funboard "Region" selector). "auto" means the
 *  client detects the country from the browser's timezone; "" means
 *  Global (no country bias). */
export const FUNBOARD_REGIONS = [
    { code: "auto",   label: "🌐 Auto-detect" },
    { code: "",       label: "🌍 Global" },
    { code: "IN",     label: "🇮🇳 India" },
    { code: "US",     label: "🇺🇸 United States" },
    { code: "GB",     label: "🇬🇧 United Kingdom" },
    { code: "CA",     label: "🇨🇦 Canada" },
    { code: "AU",     label: "🇦🇺 Australia" },
    { code: "AE",     label: "🇦🇪 UAE" },
    { code: "SA",     label: "🇸🇦 Saudi Arabia" },
    { code: "EG",     label: "🇪🇬 Egypt" },
    { code: "BR",     label: "🇧🇷 Brazil" },
    { code: "MX",     label: "🇲🇽 Mexico" },
    { code: "AR",     label: "🇦🇷 Argentina" },
    { code: "DE",     label: "🇩🇪 Germany" },
    { code: "FR",     label: "🇫🇷 France" },
    { code: "IT",     label: "🇮🇹 Italy" },
    { code: "ES",     label: "🇪🇸 Spain" },
    { code: "RU",     label: "🇷🇺 Russia" },
    { code: "CN",     label: "🇨🇳 China" },
    { code: "JP",     label: "🇯🇵 Japan" },
    { code: "KR",     label: "🇰🇷 South Korea" },
];

/** Minimal timezone → ISO2 country map for the auto-detect fallback.
 *  We only need to cover the countries where Adukoo actually pulls
 *  ingest content (see FUNBOARD_REGIONS above) — anything else will
 *  quietly return "" (Global) and the feed falls back to the global
 *  mix. Kept as a switch on the IANA area/city prefix so we don't
 *  need to enumerate every single timezone. */
export function detectFunboardCountry() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        // Direct city → country map (covers 95% of real-world browsers).
        const map = {
            "Asia/Kolkata": "IN", "Asia/Calcutta": "IN",
            "Asia/Karachi": "PK",
            "Asia/Dhaka": "BD",
            "Asia/Colombo": "LK",
            "Asia/Kathmandu": "NP",
            "Asia/Dubai": "AE",
            "Asia/Riyadh": "SA", "Asia/Mecca": "SA",
            "Africa/Cairo": "EG",
            "Europe/London": "GB", "Europe/Belfast": "GB",
            "Europe/Berlin": "DE", "Europe/Munich": "DE",
            "Europe/Paris": "FR",
            "Europe/Rome": "IT",
            "Europe/Madrid": "ES",
            "Europe/Moscow": "RU", "Europe/Kaliningrad": "RU",
            "Asia/Shanghai": "CN", "Asia/Beijing": "CN", "Asia/Hong_Kong": "HK",
            "Asia/Tokyo": "JP",
            "Asia/Seoul": "KR",
            "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Perth": "AU",
            "Pacific/Auckland": "NZ",
            "America/New_York": "US", "America/Chicago": "US", "America/Los_Angeles": "US",
            "America/Denver": "US", "America/Phoenix": "US", "America/Anchorage": "US",
            "America/Toronto": "CA", "America/Vancouver": "CA", "America/Montreal": "CA",
            "America/Mexico_City": "MX",
            "America/Sao_Paulo": "BR", "America/Bahia": "BR",
            "America/Buenos_Aires": "AR", "America/Argentina/Buenos_Aires": "AR",
        };
        if (map[tz]) return map[tz];
        // Fallback: any Asia/* Indian sub-city (Asia/Kolkata is the only
        // one shipped by tzdata for India but older browsers may serve
        // "Asia/Calcutta"; both handled above).
        if (tz.startsWith("America/Argentina/")) return "AR";
        if (tz.startsWith("America/Indiana/") || tz.startsWith("US/")) return "US";
        if (tz.startsWith("Canada/")) return "CA";
        if (tz.startsWith("Australia/")) return "AU";
        if (tz.startsWith("Europe/")) return "";  // don't guess — many small EU countries
        return "";
    } catch {
        return "";
    }
}

/** Detect the user's Funboard content language from Adukoo profile /
 *  localStorage / browser. Returns an ISO-639-1 code the backend
 *  understands (matches SAFE_SUBREDDITS keys in funboard_ingest.py). */
export function detectFunboardLanguage(user) {
    // v79.101 — focused on major-market content only. Non-listed languages
    // fall back to 'en' so the feed always has fresh, rich content.
    const supported = ["en", "hi"];
    const profile = user?.language;
    if (profile && supported.includes(profile)) return profile;
    const browser = (navigator.language || "en").split("-")[0];
    if (supported.includes(browser)) return browser;
    return "en";
}
